import { useEffect, useRef } from 'react';
import { createChart, CrosshairMode } from 'lightweight-charts';

export default function CandleChart({ data = [], liveCandle = null, markers = [], height = 320 }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height,
      layout: { background: { color: '#0f172a' }, textColor: '#94a3b8' },
      grid: { vertLines: { color: '#1e293b' }, horzLines: { color: '#1e293b' } },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: { borderColor: '#334155', timeVisible: true },
      rightPriceScale: { borderColor: '#334155' },
    });

    const series = chart.addCandlestickSeries({
      upColor: '#34d399',
      downColor: '#f87171',
      borderVisible: false,
      wickUpColor: '#34d399',
      wickDownColor: '#f87171',
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const onResize = () => chart.applyOptions({ width: containerRef.current?.clientWidth || 600 });
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); chart.remove(); };
  }, [height]);

  useEffect(() => {
    if (seriesRef.current && data.length) {
      seriesRef.current.setData(data);
    }
  }, [data]);

  // Apply buy/sell markers — only show when signal CHANGES to avoid chart flood
  useEffect(() => {
    if (!seriesRef.current) return;

    // Sort by time first
    const sorted = [...markers].sort((a, b) => a.time - b.time);

    // Deduplicate: keep only where signal changes from previous non-hold signal
    // Exception: if marker has a custom _label (arena trades) always show it
    const deduped = [];
    let lastSignal = null;
    for (const m of sorted) {
      const sig = m.signal;
      if (m._label && m._label !== `B ${Math.round((m.confidence||1)*100)}%` && m._label !== `S ${Math.round((m.confidence||1)*100)}%`) {
        // Custom label (e.g. arena trade markers) — always show
        deduped.push(m);
      } else if (sig !== 'hold' && sig !== lastSignal) {
        deduped.push(m);
        lastSignal = sig;
      }
    }

    const formatted = deduped.map(m => {
      const isBuy = m.signal === 'buy';
      const conf = Math.round((m.confidence || 1) * 100);
      return {
        time: m.time,
        position: isBuy ? 'belowBar' : 'aboveBar',
        color: m._color || (isBuy ? '#34d399' : '#f87171'),
        shape: isBuy ? 'arrowUp' : 'arrowDown',
        // Short label: just direction + confidence, no "B"/"S" prefix clutter
        text: m._label || `${conf}%`,
        size: 1,
      };
    });

    seriesRef.current.setMarkers(formatted);
  }, [markers]);

  useEffect(() => {
    if (seriesRef.current && liveCandle) {
      seriesRef.current.update(liveCandle);
    }
  }, [liveCandle]);

  return <div ref={containerRef} style={{ width: '100%', height }} />;
}
